import { Injectable, inject } from '@angular/core';
import {
  Event as RouterEvent,
  NavigationCancel,
  NavigationCancellationCode,
  NavigationEnd,
  NavigationError,
  Router,
} from '@angular/router';
import { filter, take } from 'rxjs';
import { resolveWaitTimings } from './wait-state.util';
import { DeviceSupportService } from './device-support.service';
import {
  BOOT_SPLASH_CROSSFADE_MS,
  BOOT_SPLASH_DISMISSED_CLASS,
  BOOT_SPLASH_ELEMENT_ID,
} from '../Consts/boot-splash.const';

@Injectable({ providedIn: 'root' })
export class BootSplashService {
  private readonly _router = inject(Router);
  private readonly _device = inject(DeviceSupportService);

  private _navigationSettled = false;
  private _bootstrapped = false;

  constructor() {
    this._listenForNavigationSettle();
  }

  notifyBootstrapped(): void {
    this._bootstrapped = true;
    this._tryDismiss();
  }

  private _listenForNavigationSettle(): void {
    if (this._router.navigated || this._device.blocked()) {
      this._navigationSettled = true;
      this._tryDismiss();
      return;
    }

    this._router.events
      .pipe(
        filter((event) => this._settlesFirstNavigation(event)),
        take(1),
      )
      .subscribe(() => {
        this._navigationSettled = true;
        this._tryDismiss();
      });
  }

  private _settlesFirstNavigation(event: RouterEvent): boolean {
    if (event instanceof NavigationEnd || event instanceof NavigationError) {
      return true;
    }

    if (event instanceof NavigationCancel) {
      return (
        event.code !== NavigationCancellationCode.Redirect &&
        event.code !== NavigationCancellationCode.SupersededByNewNavigation
      );
    }

    return false;
  }

  private _tryDismiss(): void {
    if (!this._navigationSettled || !this._bootstrapped) {
      return;
    }

    const { grace, floor } = resolveWaitTimings();
    const elapsed = performance.now();

    if (elapsed < grace) {
      this._removeSplashImmediately();
      return;
    }

    const remaining = grace + floor - elapsed;
    if (remaining > 0) {
      setTimeout(() => this._dismissSplash(), remaining);
      return;
    }

    this._dismissSplash();
  }

  private _dismissSplash(): void {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this._removeSplashImmediately();
      return;
    }

    const splash = document.getElementById(BOOT_SPLASH_ELEMENT_ID);
    if (!splash) {
      return;
    }

    splash.classList.add(BOOT_SPLASH_DISMISSED_CLASS);
    setTimeout(() => splash.remove(), BOOT_SPLASH_CROSSFADE_MS);
  }

  private _removeSplashImmediately(): void {
    document.getElementById(BOOT_SPLASH_ELEMENT_ID)?.remove();
  }
}
